
/* ============================================================
   MVP SIGNATURE — METAL MEDIA
   assets/mvp-metal-media.js

   SHARED VIDEO:
   mvp:SHARED:02

   One video is reused for WG, YG and RG hover.

   Without a shared video:
   Original metal-specific image/video behavior remains.

   Product-page gallery synchronization is retained.
   ============================================================ */

(() => {
  if (window.MvpMetalMedia) return;

  const WIDTHS = [360, 480, 640, 800, 1000, 1200];

  const MARKER =
    /\s*\|?\s*mvp:\s*(?:[a-z]{2}|shared)(?:\s*:\s*\d+)?\s*/gi;

  const canHover = () =>
    window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    ).matches;

  const wait = ms =>
    new Promise(resolve => setTimeout(resolve, ms));

  const findAll = (root, selector) => {
    const scope = root?.querySelectorAll ? root : document;
    const nodes = [...scope.querySelectorAll(selector)];

    if (scope.matches?.(selector)) {
      nodes.unshift(scope);
    }

    return nodes;
  };

  const getCard = el =>
    el?.closest('product-card') ||
    el?.closest('.product-card') ||
    el?.closest('.product-grid__item');

  const cardParts = card => {
    const gallery = card?.querySelector(
      '.card-gallery[data-mvp-metal-card]'
    );

    return {
      gallery,
      primary: gallery?.querySelector(
        '.mvp-card-primary-media__image'
      ),
      overlay: gallery?.querySelector(
        '.mvp-card-secondary-overlay'
      ),
      secondary: gallery?.querySelector(
        '.mvp-card-secondary-overlay__image'
      )
    };
  };

  /* ============================================================
     ALT TEXT
     ============================================================ */

  const cleanAlts = root => {
    findAll(
      root,
      'img[alt*="mvp:" i]'
    ).forEach(img => {
      img.alt = img.alt
        .replace(MARKER, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
    });
  };

  /* ============================================================
     IMAGE LOADING
     ============================================================ */

  const buildSrcset = url => {
    if (!url) return '';

    try {
      return WIDTHS.map(width => {
        const u = new URL(url, location.href);
        u.searchParams.set(
          'width',
          String(width)
        );
        return `${u.href} ${width}w`;
      }).join(', ');
    } catch {
      return '';
    }
  };

  const sizesOf = img =>
    (img?.getAttribute('sizes') || '100vw')
      .replace(/^\s*auto\s*,\s*/i, '');

  const preloadCache = new Map();

  const preload = (url, sizes) => {
    if (!url) return Promise.resolve();

    const key = `${url}|${sizes}`;

    if (preloadCache.has(key)) {
      return preloadCache.get(key);
    }

    const img = new Image();

    img.decoding = 'async';

    if (sizes) img.sizes = sizes;

    img.srcset = buildSrcset(url);
    img.src = url;

    const task = img.decode
      ? img.decode().catch(() => {})
      : new Promise(resolve => {
          img.onload = img.onerror = resolve;
        });

    preloadCache.set(key, task);
    return task;
  };

  const setImage = (img, url, alt) => {
    if (!img || !url) return;

    if (img.dataset.mvpUrl !== url) {
      img.dataset.mvpUrl = url;
      img.loading = 'eager';
      img.srcset = buildSrcset(url);
      img.src = url;
    }

    if (alt) img.alt = alt;
  };

  /* ============================================================
     PRODUCT VARIANT LINKS
     ============================================================ */

  const updateLinks = (card, variantId, primaryUrl) => {
    if (!card) return;

    card.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');

      if (!href || !href.includes('/products/')) return;

      try {
        const url = new URL(href, location.origin);

        if (variantId) {
          url.searchParams.set('variant', variantId);
        } else {
          url.searchParams.delete('variant');
        }

        link.setAttribute(
          'href',
          url.pathname + url.search + url.hash
        );
      } catch {}
    });

    const productCard = card.matches('product-card')
      ? card
      : card.querySelector('product-card');

    if (
      productCard?.hasAttribute('data-featured-media-url') &&
      primaryUrl
    ) {
      productCard.setAttribute(
        'data-featured-media-url',
        primaryUrl
      );
    }
  };

  /* ============================================================
     SHARED VIDEO SOURCE

     Priority:
     1. Gallery data-mvp-shared-video
     2. Swatch group data-mvp-shared-video
     3. Original per-metal secondary media

     A product without shared media is unchanged.
     ============================================================ */

  // A single explicitly tagged media item (image OR video) is shared
  // across every metal. Video takes priority only when both are tagged.
  const sharedMediaFor = group => {
    const { gallery } = cardParts(getCard(group));
    const source = gallery?.dataset.mvpSharedVideo || group.dataset.mvpSharedVideo || '';
    const poster = gallery?.dataset.mvpSharedPoster || group.dataset.mvpSharedPoster || '';
    const image = gallery?.dataset.mvpSharedImage || group.dataset.mvpSharedImage || '';
    const alt = gallery?.dataset.mvpSharedAlt || group.dataset.mvpSharedAlt || '';
    if (source) return {
      secondaryKind: 'video', secondaryVideo: source,
      secondary: poster, secondaryAlt: alt
    };
    if (image) return {
      secondaryKind: 'image', secondaryVideo: '',
      secondary: image, secondaryAlt: alt
    };
    return null;
  };

  const hoverMode = group => {
    const shared = sharedMediaFor(group);

    if (shared) return 'shared';

    const owner = group.closest(
      '[data-mvp-card-hover-mode]'
    );

    const setting =
      owner?.dataset.mvpCardHoverMode ||
      group.dataset.mvpCardHoverMode ||
      'per-metal';

    return setting === 'shared'
      ? 'shared'
      : 'per-metal';
  };

  const effectiveHoverData = (group, data) => {
    const shared = sharedMediaFor(group);

    if (shared) {
      return { ...data, ...shared };
    }

    if (hoverMode(group) === 'shared') {
      const donor = findAll(group, '[data-mvp-swatch]').find(btn =>
        (btn.dataset.secondaryKind === 'video' && btn.dataset.secondaryVideo) ||
        (btn.dataset.secondaryKind !== 'video' && btn.dataset.secondary)
      );

      if (donor) {
        return {
          ...data,
          secondaryKind: donor.dataset.secondaryKind || 'image',
          secondaryVideo: donor.dataset.secondaryVideo || '',
          secondary: donor.dataset.secondary || '',
          secondaryAlt: donor.dataset.secondaryAlt || ''
        };
      }
    }

    return data;
  };

  /* ============================================================
     VIDEO PLAYER
     ============================================================ */

  const playVideo = video => {
    if (!video || !canHover()) return;

    if (
      !video.getAttribute('src') &&
      !video.currentSrc
    ) return;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;

    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');

    const promise = video.play();

    if (promise?.catch) {
      promise.catch(() => {});
    }
  };

  const pauseVideo = video => {
    if (video && !video.paused) {
      video.pause();
    }
  };

  const isHovered = gallery =>
    gallery?.matches(':hover') ||
    gallery?.closest('product-card')?.matches(':hover') ||
    gallery?.closest('.product-card')?.matches(':hover');

  const videoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const video = entry.target;

            const overlay = video.closest(
              '.mvp-card-secondary-overlay'
            );

            const gallery = video.closest(
              '.card-gallery[data-mvp-metal-card]'
            );

            if (
              entry.isIntersecting &&
              overlay &&
              !overlay.hidden &&
              overlay.dataset.kind === 'video' &&
              isHovered(gallery)
            ) {
              playVideo(video);
            } else {
              pauseVideo(video);
            }
          });
        }, {
          rootMargin: '150px 0px',
          threshold: 0.01
        })
      : null;

  const watchVideos = root => {
    findAll(
      root,
      '.mvp-card-secondary-overlay__video:not([data-mvp-watched])'
    ).forEach(video => {
      video.dataset.mvpWatched = '1';

      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      video.addEventListener(
        'contextmenu',
        e => e.preventDefault()
      );

      if (videoObserver) {
        videoObserver.observe(video);
      }
    });
  };

  /* ============================================================
     VIDEO LAYER CSS

     Preserve original right-to-left slide animation.
     Only fix video/poster stacking.
     ============================================================ */

  const installStyles = () => {
    if (document.getElementById('mvp-shared-video-fix')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'mvp-shared-video-fix';

    style.textContent = `
      .card-gallery[data-mvp-metal-card="true"]
      .mvp-card-secondary-overlay[data-kind="video"] {
        position: absolute !important;
        inset: 0 !important;
        z-index: 2 !important;
        overflow: hidden !important;
        pointer-events: none !important;
      }

      .card-gallery[data-mvp-metal-card="true"]
      .mvp-card-secondary-overlay[data-kind="video"]
      .mvp-card-secondary-overlay__image {
        opacity: 0 !important;
        visibility: hidden !important;
      }

      .card-gallery[data-mvp-metal-card="true"]
      .mvp-card-secondary-overlay__video {
        position: absolute !important;
        inset: 0 !important;
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        max-height: none !important;
        object-fit: var(--mvp-media-fit, cover) !important;
        object-position: center !important;
        z-index: 3 !important;
        pointer-events: none !important;
      }

      .card-gallery[data-mvp-metal-card="true"]
      .mvp-card-secondary-overlay[data-kind="image"]
      .mvp-card-secondary-overlay__video {
        display: none !important;
      }

      .card-gallery[data-mvp-metal-card="true"]
      .mvp-card-secondary-overlay[hidden] {
        display: none !important;
      }
    `;

    document.head.appendChild(style);
  };

  /* ============================================================
     CREATE / REUSE EXISTING VIDEO ELEMENT
     ============================================================ */

  const setSecondaryVideo = (overlay, data) => {
    if (!overlay || !data.secondaryVideo) {
      return false;
    }

    let video = overlay.querySelector(
      '.mvp-card-secondary-overlay__video'
    );

    if (!video) {
      video = document.createElement('video');

      video.className =
        'mvp-card-secondary-overlay__video';

      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;

      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute(
        'disablepictureinpicture',
        ''
      );
      video.setAttribute('tabindex', '-1');

      video.preload = 'metadata';

      overlay.appendChild(video);
    }

    if (data.secondary) {
      video.poster = data.secondary;
    }

    if (
      video.getAttribute('src') !==
      data.secondaryVideo
    ) {
      pauseVideo(video);
      video.setAttribute('src', data.secondaryVideo);
      video.load();
    }

    overlay.dataset.kind = 'video';
    overlay.hidden = false;

    watchVideos(overlay);

    const gallery = overlay.closest(
      '.card-gallery[data-mvp-metal-card]'
    );

    if (isHovered(gallery)) {
      playVideo(video);
    }

    return true;
  };

  /* ============================================================
     APPLY SELECTED METAL'S HOVER MEDIA
     ============================================================ */

  const applyHoverMedia = (group, selected) => {
    const { overlay, secondary } = cardParts(
      getCard(group)
    );

    if (!overlay || !selected) return;

    const data = effectiveHoverData(group, selected);

    const oldVideo = overlay.querySelector(
      '.mvp-card-secondary-overlay__video'
    );

    if (
      data.secondaryKind === 'video' &&
      data.secondaryVideo
    ) {
      if (secondary && data.secondary) {
        setImage(
          secondary,
          data.secondary,
          data.secondaryAlt
        );
      }

      setSecondaryVideo(overlay, data);

    } else if (data.secondary && secondary) {
      pauseVideo(oldVideo);

      setImage(
        secondary,
        data.secondary,
        data.secondaryAlt
      );

      overlay.dataset.kind = 'image';
      overlay.hidden = false;

    } else {
      pauseVideo(oldVideo);
      overlay.hidden = true;
    }
  };

  /* ============================================================
     INITIAL CARD HOVER

     Shared video available on first page load,
     without requiring a metal swatch click.
     ============================================================ */

  const prepareCardHover = group => {
    const { overlay } = cardParts(getCard(group));

    if (!overlay) return;

    const selected =
      group.querySelector(
        '[data-mvp-swatch][aria-pressed="true"]'
      ) ||
      group.querySelector('[data-mvp-swatch]');

    if (!selected) return;

    const shared = sharedMediaFor(group);

    const key = [
      selected.dataset.metal || '',
      shared?.secondaryVideo || shared?.secondary || '',
      hoverMode(group)
    ].join('|');

    if (group.dataset.mvpHoverState === key) {
      return;
    }

    group.dataset.mvpHoverState = key;

    if (shared) {
      applyHoverMedia(group, selected.dataset);
      return;
    }

    // For ordinary products preserve the initial Liquid image, but
    // initialize videos and explicit shared-mode swatch donors.
    if ((selected.dataset.secondaryKind === 'video' &&
         selected.dataset.secondaryVideo) || hoverMode(group) === 'shared') {
      applyHoverMedia(group, selected.dataset);
    }
  };

  /* ============================================================
     METAL SWATCH CLICK
     ============================================================ */

  const selectSwatch = async button => {
    const group = button.closest(
      '[data-mvp-card-swatches]'
    );

    if (!group) return;

    if (button.getAttribute('aria-pressed') === 'true') {
      prepareCardHover(group);
      return;
    }

    const card = getCard(group);
    const { primary } = cardParts(card);

    const data = button.dataset;

    const token = String(
      (Number(group.dataset.mvpToken) || 0) + 1
    );

    group.dataset.mvpToken = token;

    group.querySelectorAll('[data-mvp-swatch]')
      .forEach(swatch => {
        swatch.setAttribute(
          'aria-pressed',
          String(swatch === button)
        );
      });

    group.dataset.currentMetal = data.metal || '';

    updateLinks(
      card,
      data.variantId,
      data.primary
    );

    await Promise.race([
      preload(data.primary, sizesOf(primary)),
      wait(300)
    ]);

    if (group.dataset.mvpToken !== token) {
      return;
    }

    setImage(
      primary,
      data.primary,
      data.primaryAlt
    );

    applyHoverMedia(group, data);

    group.dataset.mvpHoverState = '';
    prepareCardHover(group);
  };

  document.addEventListener(
    'click',
    event => {
      const button = event.target.closest?.(
        '[data-mvp-swatch]'
      );

      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      selectSwatch(button);
    },
    true
  );

  /* ============================================================
     HOVER PLAY / PAUSE
     ============================================================ */

  document.addEventListener(
    'pointerover',
    event => {
      if (!canHover()) return;

      const gallery = event.target.closest?.(
        '.card-gallery[data-mvp-metal-card]'
      );

      if (!gallery) return;

      const group = getCard(gallery)?.querySelector(
        '[data-mvp-card-swatches]'
      );

      if (group) prepareCardHover(group);

      const video = gallery.querySelector(
        '.mvp-card-secondary-overlay[data-kind="video"] .mvp-card-secondary-overlay__video'
      );

      if (video) playVideo(video);
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerout',
    event => {
      const gallery = event.target.closest?.(
        '.card-gallery[data-mvp-metal-card]'
      );

      if (!gallery) return;

      if (
        event.relatedTarget &&
        gallery.contains(event.relatedTarget)
      ) return;

      const video = gallery.querySelector(
        '.mvp-card-secondary-overlay__video'
      );

      pauseVideo(video);
    },
    { passive: true }
  );

  /* ============================================================
     PRELOAD METAL PRIMARY IMAGES
     ============================================================ */

  const warm = event => {
    const button = event.target.closest?.(
      '[data-mvp-swatch]'
    );

    if (!button || button.dataset.mvpWarm) {
      return;
    }

    button.dataset.mvpWarm = '1';

    const { primary } = cardParts(
      getCard(button)
    );

    const sizes = sizesOf(primary);

    preload(button.dataset.primary, sizes);

    if (
      button.dataset.secondaryKind !== 'video'
    ) {
      preload(button.dataset.secondary, sizes);
    }
  };

  document.addEventListener(
    'pointerover',
    warm,
    { passive: true }
  );

  document.addEventListener('focusin', warm);

  /* ============================================================
     PRODUCT PAGE GALLERY SYNC
     Preserves existing section-rendering behavior.
     ============================================================ */

  const resolveMetal = (raw, groups) => {
    const list = groups || '';

    if (raw && list.includes(`,${raw},`)) {
      return raw;
    }

    if (list.includes(',WG,')) {
      return 'WG';
    }

    return list.split(',').filter(Boolean)[0] || '';
  };

  const readVariantMap = gallery => {
    const script = gallery.querySelector(
      'script[data-mvp-variant-metals]'
    );

    if (!script) return {};

    try {
      return JSON.parse(script.textContent) || {};
    } catch {
      return {};
    }
  };

  const currentVariantId = event => {
    const fromEvent =
      event?.detail?.resource?.id;

    if (fromEvent) {
      return String(fromEvent);
    }

    const input = document.querySelector(
      'form[action*="/cart/add"] [name="id"]'
    );

    if (input?.value) {
      return String(input.value);
    }

    return new URL(location.href)
      .searchParams.get('variant') || '';
  };

  const activeSeq = gallery => {
    const slide = gallery.querySelector(
      'slideshow-slide[aria-hidden="false"]'
    );

    return slide?.getAttribute(
      'data-mvp-seq'
    ) || null;
  };

  const restoreSeq = (gallery, seq) => {
    if (!seq) return;

    const slides = [
      ...gallery.querySelectorAll('slideshow-slide')
    ];

    const index = slides.findIndex(
      slide =>
        slide.getAttribute('data-mvp-seq') === seq
    );

    if (index <= 0) return;

    requestAnimationFrame(() => {
      const slideshow = gallery.querySelector(
        'slideshow-component'
      );

      try {
        if (
          slideshow &&
          typeof slideshow.select === 'function'
        ) {
          slideshow.select(
            index,
            undefined,
            { animate: false }
          );
        }
      } catch {}
    });
  };

  const requests = {};

  const syncGallery = async (gallery, variantId) => {
    const map = readVariantMap(gallery);

    if (
      !Object.prototype.hasOwnProperty.call(
        map,
        variantId
      )
    ) return;

    const want = resolveMetal(
      map[variantId],
      gallery.dataset.mvpGroups
    );

    if (
      !want ||
      want === gallery.dataset.mvpMetal
    ) return;

    const key =
      gallery.dataset.mvpBlockId || 'gallery';

    const mine = (
      requests[key] =
        (requests[key] || 0) + 1
    );

    const seq = activeSeq(gallery);

    const liveGallery = () =>
      document.querySelector(
        `media-gallery[data-mvp-block-id="${CSS.escape(key)}"]`
      ) || gallery;

    gallery.classList.add(
      'mvp-gallery--switching'
    );

    try {
      const url = new URL(
        gallery.dataset.mvpProductUrl,
        location.origin
      );

      url.searchParams.set(
        'variant',
        variantId
      );

      url.searchParams.set(
        'section_id',
        gallery.dataset.mvpSectionId
      );

      const response = await fetch(
        url.href,
        { credentials: 'same-origin' }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();

      if (requests[key] !== mine) return;

      const live = liveGallery();

      if (live.dataset.mvpMetal === want) {
        live.classList.remove(
          'mvp-gallery--switching'
        );
        return;
      }

      const doc = new DOMParser()
        .parseFromString(html, 'text/html');

      const fresh = doc.querySelector(
        `media-gallery[data-mvp-block-id="${CSS.escape(key)}"]`
      );

      if (!fresh) {
        throw new Error(
          'Gallery not found in section response'
        );
      }

      fresh.classList.add(
        'mvp-gallery--switching'
      );

      const node = document.importNode(fresh, true);

      live.replaceWith(node);
      cleanAlts(node);

      node.querySelectorAll('video[autoplay]')
        .forEach(video => {
          video.muted = true;

          const promise = video.play();

          if (promise?.catch) {
            promise.catch(() => {});
          }
        });

      if (
        node.dataset.mvpGalleryMode !== 'all_first'
      ) {
        restoreSeq(node, seq);
      }

      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          node.classList.remove(
            'mvp-gallery--switching'
          )
        )
      );

    } catch (error) {
      liveGallery().classList.remove(
        'mvp-gallery--switching'
      );

      console.warn(
        '[MVP metal media] Gallery refresh failed:',
        error
      );
    }
  };

  const syncGalleries = variantId => {
    if (!variantId) return;

    document.querySelectorAll(
      'media-gallery[data-mvp-metal]'
    ).forEach(gallery => {
      syncGallery(gallery, String(variantId));
    });
  };

  document.addEventListener(
    'variant:update',
    event => {
      const id = currentVariantId(event);

      setTimeout(() => {
        syncGalleries(
          id || currentVariantId()
        );
      }, 60);
    }
  );

  document.addEventListener(
    'change',
    event => {
      if (!event.target.closest?.('variant-picker')) {
        return;
      }

      setTimeout(() => {
        syncGalleries(currentVariantId());
      }, 900);
    }
  );

  /* ============================================================
     DYNAMIC CARDS / INFINITE SCROLL
     ============================================================ */

  const init = root => {
    installStyles();
    cleanAlts(root);
    watchVideos(root);

    findAll(root, '[data-mvp-card-swatches]')
      .forEach(prepareCardHover);
  };

  let pending = [];
  let scheduled = false;

  const flush = () => {
    const nodes = pending;

    pending = [];
    scheduled = false;

    nodes.forEach(node => {
      if (node.isConnected) {
        init(node);
      }
    });
  };

  new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) {
          pending.push(node);
        }
      });
    });

    if (pending.length && !scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  }).observe(
    document.documentElement,
    { childList: true, subtree: true }
  );

  document.addEventListener(
    'shopify:section:load',
    event => init(event.target)
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(document),
      { once: true }
    );
  } else {
    init(document);
  }

  window.MvpMetalMedia = {
    version: 'shared-image-video-7.0',
    cleanAlts,
    resolveMetal,
    syncGalleries,
    selectSwatch,
    sharedMediaFor,
    hoverMode,
    init
  };
})();
